<template>
  <div class="login-page">
    <div class="login-card">
      <div class="login-logo">
        <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
          <rect width="36" height="36" rx="8" fill="var(--accent)"/>
          <path d="M10 14h16M10 18h10M10 22h13" stroke="#fff" stroke-width="2.5" stroke-linecap="round"/>
        </svg>
        <span>DevTask</span>
      </div>
      <h1>{{ t('login.title') }}</h1>
      <p class="login-sub">{{ t('login.sub') }}</p>

      <form @submit.prevent="submit">
        <div class="form-group">
          <label>{{ t('login.identifier') }}</label>
          <input
            v-model="identifier"
            type="text"
            class="form-control"
            :placeholder="t('login.identifierPlaceholder')"
            autocomplete="username"
            required
          />
        </div>
        <div class="form-group">
          <label>{{ t('login.password') }}</label>
          <input
            v-model="password"
            type="password"
            class="form-control"
            placeholder="••••••••"
            autocomplete="current-password"
            required
          />
        </div>

        <p v-if="error" class="login-error">{{ error }}</p>

        <button type="submit" class="btn btn-primary login-btn" :disabled="loading">
          <span v-if="loading">{{ t('login.signingIn') }}</span>
          <span v-else>{{ t('login.signIn') }}</span>
        </button>
      </form>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import api from '@/api'
import { useAuth } from '@/composables/useAuth'
import { useI18n } from '@/composables/useI18n'

export default {
  name: 'LoginView',
  setup() {
    const router         = useRouter()
    const { setSession } = useAuth()
    const { t }          = useI18n()

    const identifier = ref('')
    const password   = ref('')
    const error      = ref('')
    const loading    = ref(false)

    async function submit() {
      error.value   = ''
      loading.value = true
      try {
        const id = identifier.value.trim()
        // Send as email if it contains @, otherwise as username (external client)
        const credentials = id.includes('@')
          ? { email: id, password: password.value }
          : { username: id, password: password.value }

        const res = await api.login(credentials)
        setSession(res.data.token, res.data.user)
        router.push({ name: 'dashboard' })
      } catch (e) {
        error.value = e.message || 'Login failed'
      } finally {
        loading.value = false
      }
    }

    return { identifier, password, error, loading, submit, t }
  },
}
</script>

<style scoped>
.login-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg);
  padding: 20px;
}
.login-card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 40px 36px;
  width: 100%;
  max-width: 400px;
  box-shadow: var(--shadow);
}
.login-logo {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 24px;
  font-size: 20px;
  font-weight: 700;
  color: var(--text);
}
.login-card h1 {
  font-size: 22px;
  font-weight: 700;
  margin-bottom: 6px;
}
.login-sub {
  color: var(--muted);
  font-size: 13px;
  margin-bottom: 28px;
}
.login-btn {
  width: 100%;
  justify-content: center;
  padding: 11px;
  font-size: 14px;
  margin-top: 4px;
}
.login-error {
  color: var(--danger);
  font-size: 13px;
  margin-bottom: 12px;
  padding: 8px 12px;
  background: rgba(239,68,68,.1);
  border-radius: 6px;
  border: 1px solid rgba(239,68,68,.3);
}
</style>
